<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import Filters from './Filters.vue'
import { type FilterDefinition, FilterDefinitionTypeEnum } from './filters'
import { Button } from "@/shadcn/components/ui/button";
import qs from "qs";
import { stripToggleTokens } from './composables/useFilterValue';

const props = withDefaults(
  defineProps<{
    availableFilters: FilterDefinition[]
    action: string
    baseFilters?: Record<string, any>
    showFiltersSummary?: boolean
    showResetButton?: boolean
    autoSubmit?: boolean
  }>(),
  {
    baseFilters: () => ({}),
    showFiltersSummary: false,
    showResetButton: true,
    autoSubmit: false,
  }
)

// Store all filter values
const filters = ref<Record<string, string | boolean>>({})

// Preserved query parameters from current URL
const preservedParams = ref<Record<string, string>>({})

// Form submission state
const isSubmitting = ref(false)

/**
 * Check if any filters are active
 */
const hasActiveFilters = computed(() => {
  return Object.keys(filters.value).length > 0
})

/**
 * Initialize filters and preserved params from URL query string
 */
onMounted(() => {
  const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  // Load all current filter values from URL
  props.availableFilters.forEach(filter => {
    const value = parsed.filter?.[filter.name]
    if (value) {
      filters.value[filter.name] = value
    } else if (filter.default) {
      filters.value[filter.name] = filter.default
    }
  })
})

/**
 * Watch filters for auto-submit
 */
watch(filters, () => {
  if (props.autoSubmit) {
    submitForm()
  }
}, { deep: true })

/**
 * Get filter label for display
 */
function getFilterLabel(name: string): string {
  const filter = props.availableFilters.find(f => f.name === name)
  return filter?.label || name
}

/**
 * Format filter value for display
 */
function formatFilterValue(name: string, value: string | boolean): string {
  const filter = props.availableFilters.find(f => f.name === name)

  if (!filter) {
    return String(value)
  }

  if (filter.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return value === '1' || value === true ? 'Yes' : 'No'
  }

  if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filter.type)) {
    // The toggle token rides along the values but names no option of its own.
    const values = stripToggleTokens(String(value).split(','))
    const labels = values
      .map(v => filter.options?.find(opt => String(opt.value) === v)?.label || v)
      .filter(Boolean)

    if (labels.length > 3) {
      return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`
    }

    return labels.join(', ')
  }

  if (filter.type === FilterDefinitionTypeEnum.PERIOD) {
    const parts = String(value).split(':')
    const from = parts[0] || ''
    const to = parts[1] || ''
    if (from && to) return `${from} → ${to}`
    if (from) return `From: ${from}`
    if (to) return `To: ${to}`
  }

  return String(value)
}

/**
 * Reset all filters
 */
function resetFilters(): void {
  filters.value = {}

  if (props.autoSubmit) {
    submitForm()
  }
}

/**
 * Submit the form programmatically
 */
function submitForm(): void {
  if (isSubmitting.value) return

  isSubmitting.value = true

  // Use nextTick to ensure DOM is updated with hidden inputs
  setTimeout(() => {
    const form = document.querySelector(`form[action="${props.action}"]`) as HTMLFormElement
    if (form) {
      form.submit()
    }
  }, 0)
}
</script>
<template>
  <form :action="action" method="GET" class="tw:space-y-4">
    <!-- Hidden inputs for all filter values -->
    <input
      v-for="(value, name) in filters"
      :key="name"
      type="hidden"
      :name="'filter[' + name + ']'"
      :value="value"
    />

    <!-- Preserve existing query parameters -->
    <input
      v-for="(value, name) in preservedParams"
      :key="`preserved-${name}`"
      type="hidden"
      :name="name"
      :value="value"
    />

    <!-- Filters component -->
    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      <Filters
        v-model="filters"
        :available-filters="availableFilters"
        :base-filters="baseFilters"
      />

      <!-- Submit button -->
      <Button
        variant="outline"
        v-if="!autoSubmit"
        type="submit"
        size="sm"
        :disabled="isSubmitting"
      >
        {{ $t('Apply filters') }}
      </Button>

      <!-- Reset button -->
      <Button
        variant="ghost-destructive"
        size="sm"
        v-if="showResetButton && hasActiveFilters"
        type="button"
        @click="resetFilters"
      >
        {{ $t('Reset') }}
      </Button>
    </div>

    <!-- Optional: Display active filters summary -->
    <div v-if="showFiltersSummary && hasActiveFilters" class="tw:mt-4 tw:p-3 tw:bg-gray-50 tw:rounded tw:text-sm">
      <strong class="tw:block tw:mb-2">{{ $t('Active filters') }}:</strong>
      <div class="tw:flex tw:flex-wrap tw:gap-2">
        <span
          v-for="(value, name) in filters"
          :key="name"
          class="tw:inline-flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:bg-blue-100 tw:text-blue-800 tw:rounded"
        >
          <strong>{{ getFilterLabel(name) }}:</strong>
          {{ formatFilterValue(name, value) }}
        </span>
      </div>
    </div>
  </form>
</template>
